// frontend/src/components/AccountSettings.jsx
import React, { useState } from 'react';
import {
  X,
  User,
  Mail,
  Lock,
  CreditCard,
  CheckCircle,
  AlertCircle,
  Zap,
  Eye,
  EyeOff,
  ChevronRight,
  Sun,
  Moon,
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';

// ─── Small reusable inline-form wrapper ──────────────────────────────────────
const SectionRow = ({ icon: Icon, label, value, children, onExpand, expanded }) => (
  <div className="border border-gray-700/40 rounded-xl overflow-hidden">
    <button
      type="button"
      onClick={onExpand}
      className="w-full flex items-center justify-between px-4 py-3 bg-gray-800/40 hover:bg-gray-800/70 transition-colors text-left"
    >
      <div className="flex items-center gap-3 min-w-0">
        <Icon className="h-4 w-4 text-gray-400 flex-shrink-0" />
        <div className="min-w-0">
          <p className="text-xs text-gray-500">{label}</p>
          <p className="text-sm text-white truncate">{value}</p>
        </div>
      </div>
      <ChevronRight
        className={`h-4 w-4 text-gray-500 flex-shrink-0 transition-transform ${expanded ? 'rotate-90' : ''}`}
      />
    </button>
    {expanded && <div className="px-4 pb-4 pt-3 bg-gray-800/20">{children}</div>}
  </div>
);

// ─── Main component ───────────────────────────────────────────────────────────
const AccountSettings = ({ onClose }) => {
  const { user, profile, isPremium, isExempt, fetchProfile, updateEmail, updatePassword } = useAuth();
  const { isDark, toggleTheme } = useTheme();

  // Which inline form is open: null | 'email' | 'password' | 'cancel'
  const [expanded, setExpanded] = useState(null);
  const toggle = (section) => setExpanded((prev) => (prev === section ? null : section));

  // ── Email change state ──────────────────────────────────────────────────────
  const [newEmail,      setNewEmail]      = useState('');
  const [emailLoading,  setEmailLoading]  = useState(false);
  const [emailMsg,      setEmailMsg]      = useState({ type: '', text: '' });

  const handleEmailChange = async (e) => {
    e.preventDefault();
    setEmailLoading(true);
    setEmailMsg({ type: '', text: '' });
    const { error } = await updateEmail(newEmail);
    setEmailLoading(false);
    if (error) {
      setEmailMsg({ type: 'error', text: error.message });
    } else {
      setEmailMsg({
        type: 'success',
        text: `Verification link sent to ${newEmail}. Click it to confirm the change.`,
      });
      setNewEmail('');
    }
  };

  // ── Password change state ───────────────────────────────────────────────────
  const [newPassword,    setNewPassword]    = useState('');
  const [confirmPw,      setConfirmPw]      = useState('');
  const [showPw,         setShowPw]         = useState(false);
  const [pwLoading,      setPwLoading]      = useState(false);
  const [pwMsg,          setPwMsg]          = useState({ type: '', text: '' });

  const handlePasswordChange = async (e) => {
    e.preventDefault();
    if (newPassword !== confirmPw) {
      setPwMsg({ type: 'error', text: 'Passwords do not match' });
      return;
    }
    if (newPassword.length < 6) {
      setPwMsg({ type: 'error', text: 'Password must be at least 6 characters' });
      return;
    }
    setPwLoading(true);
    setPwMsg({ type: '', text: '' });
    const { error } = await updatePassword(newPassword);
    setPwLoading(false);
    if (error) {
      setPwMsg({ type: 'error', text: error.message });
    } else {
      setPwMsg({ type: 'success', text: 'Password updated successfully.' });
      setNewPassword('');
      setConfirmPw('');
    }
  };

  // ── Subscription state ──────────────────────────────────────────────────────
  const [cancelConfirm,  setCancelConfirm]  = useState(false);
  const [cancelLoading,  setCancelLoading]  = useState(false);
  const [cancelMsg,      setCancelMsg]      = useState({ type: '', text: '' });
  const [subLoading,     setSubLoading]     = useState(false);
  const [subError,       setSubError]       = useState('');

  const handleCancel = async () => {
    setCancelLoading(true);
    setCancelMsg({ type: '', text: '' });
    try {
      const res = await fetch('/api/cancel-subscription', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ userId: user?.id }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Could not cancel. Please try again.');
      setCancelMsg({
        type: 'success',
        text: "Subscription cancelled. You'll keep access until the end of your billing period.",
      });
      setCancelConfirm(false);
      if (user?.id) fetchProfile(user.id);
    } catch (err) {
      setCancelMsg({ type: 'error', text: err.message });
    } finally {
      setCancelLoading(false);
    }
  };

  const handleSubscribe = async () => {
    setSubLoading(true);
    setSubError('');
    try {
      const res = await fetch('/api/create-checkout-session', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: user?.email, userId: user?.id }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Something went wrong.');
      window.location.href = data.url;
    } catch (err) {
      setSubError(err.message);
      setSubLoading(false);
    }
  };

  const tier = isExempt ? 'Complimentary' : isPremium ? 'Premium · $2.49/mo' : 'Free';
  const tierColor = isPremium
    ? 'text-green-400 bg-green-500/20 border-green-500/40'
    : 'text-gray-400 bg-gray-700/50 border-gray-600/50';

  return (
    <div
      className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4"
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <div className="bg-gray-900 border border-gray-700/60 rounded-2xl w-full max-w-md shadow-2xl max-h-[90vh] flex flex-col">

        {/* Header */}
        <div className="flex items-center justify-between px-6 py-5 border-b border-gray-700/50 flex-shrink-0">
          <h2 className="text-lg font-bold text-white">Account Settings</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-white transition-colors">
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Scrollable body */}
        <div className="overflow-y-auto flex-1 px-6 py-6 space-y-6">

          {/* ── Profile ─────────────────────────────────────────────────── */}
          <section>
            <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3">
              Profile
            </h3>
            <div className="space-y-2">

              {/* Username (read-only display) */}
              <div className="flex items-center gap-3 bg-gray-800/40 rounded-xl px-4 py-3 border border-gray-700/40">
                <User className="h-4 w-4 text-gray-400 flex-shrink-0" />
                <div>
                  <p className="text-xs text-gray-500">Username</p>
                  <p className="text-sm text-white">{profile?.username || user?.email?.split('@')[0]}</p>
                </div>
              </div>

              {/* Change email */}
              <SectionRow
                icon={Mail}
                label="Email address"
                value={user?.email}
                expanded={expanded === 'email'}
                onExpand={() => toggle('email')}
              >
                {emailMsg.text && (
                  <div className={`mb-3 p-3 rounded-lg text-xs ${
                    emailMsg.type === 'success'
                      ? 'bg-green-500/10 border border-green-500/30 text-green-300'
                      : 'bg-red-500/10 border border-red-500/30 text-red-300'
                  }`}>
                    {emailMsg.text}
                  </div>
                )}
                {!emailMsg.type && (
                  <form onSubmit={handleEmailChange} className="space-y-3">
                    <input
                      type="email"
                      value={newEmail}
                      onChange={(e) => setNewEmail(e.target.value)}
                      placeholder="New email address"
                      required
                      className="w-full px-3 py-2 bg-gray-800 border border-gray-600/50 rounded-lg text-sm text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-orange-500"
                    />
                    <button
                      type="submit"
                      disabled={emailLoading || !newEmail}
                      className="w-full bg-orange-500 hover:bg-orange-400 disabled:opacity-50 text-white text-sm font-semibold py-2 rounded-lg transition-colors disabled:cursor-not-allowed"
                    >
                      {emailLoading ? 'Sending verification…' : 'Send verification link'}
                    </button>
                    <p className="text-xs text-gray-500">
                      A verification link will be sent to your new address. Your email changes after you click it.
                    </p>
                  </form>
                )}
              </SectionRow>

            </div>
          </section>

          {/* ── Appearance ──────────────────────────────────────────────── */}
          <section>
            <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3">
              Appearance
            </h3>
            <div className="flex items-center justify-between bg-gray-800/40 rounded-xl px-4 py-3 border border-gray-700/40">
              <div className="flex items-center gap-3">
                {isDark
                  ? <Moon className="h-4 w-4 text-gray-400 flex-shrink-0" />
                  : <Sun className="h-4 w-4 text-orange-400 flex-shrink-0" />}
                <div>
                  <p className="text-xs text-gray-500">Theme</p>
                  <p className="text-sm text-white">{isDark ? 'Dark' : 'Light'}</p>
                </div>
              </div>
              {/* Pill toggle */}
              <button
                type="button"
                onClick={toggleTheme}
                aria-label={isDark ? 'Switch to light mode' : 'Switch to dark mode'}
                className={`relative flex-shrink-0 w-12 h-6 rounded-full transition-colors duration-300 focus:outline-none focus-visible:ring-2 focus-visible:ring-orange-500 ${
                  isDark ? 'bg-gray-600' : 'bg-orange-500'
                }`}
              >
                <span
                  className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full shadow-sm transition-transform duration-300 ${
                    isDark ? 'translate-x-0' : 'translate-x-6'
                  }`}
                />
              </button>
            </div>
          </section>

          {/* ── Security ────────────────────────────────────────────────── */}
          <section>
            <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3">
              Security
            </h3>

            <SectionRow
              icon={Lock}
              label="Password"
              value="••••••••"
              expanded={expanded === 'password'}
              onExpand={() => toggle('password')}
            >
              {pwMsg.text && (
                <div className={`mb-3 p-3 rounded-lg text-xs ${
                  pwMsg.type === 'success'
                    ? 'bg-green-500/10 border border-green-500/30 text-green-300'
                    : 'bg-red-500/10 border border-red-500/30 text-red-300'
                }`}>
                  {pwMsg.text}
                </div>
              )}
              {!pwMsg.type && (
                <form onSubmit={handlePasswordChange} className="space-y-3">
                  <div className="relative">
                    <input
                      type={showPw ? 'text' : 'password'}
                      value={newPassword}
                      onChange={(e) => setNewPassword(e.target.value)}
                      placeholder="New password"
                      required
                      className="w-full px-3 py-2 pr-10 bg-gray-800 border border-gray-600/50 rounded-lg text-sm text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-orange-500"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPw(!showPw)}
                      className="absolute right-3 top-2.5 text-gray-400 hover:text-white"
                    >
                      {showPw ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </button>
                  </div>
                  <input
                    type={showPw ? 'text' : 'password'}
                    value={confirmPw}
                    onChange={(e) => setConfirmPw(e.target.value)}
                    placeholder="Confirm new password"
                    required
                    className="w-full px-3 py-2 bg-gray-800 border border-gray-600/50 rounded-lg text-sm text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-orange-500"
                  />
                  <button
                    type="submit"
                    disabled={pwLoading || !newPassword || !confirmPw}
                    className="w-full bg-orange-500 hover:bg-orange-400 disabled:opacity-50 text-white text-sm font-semibold py-2 rounded-lg transition-colors disabled:cursor-not-allowed"
                  >
                    {pwLoading ? 'Updating…' : 'Update password'}
                  </button>
                </form>
              )}
            </SectionRow>
          </section>

          {/* ── Subscription ────────────────────────────────────────────── */}
          <section>
            <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3">
              Subscription
            </h3>
            <div className="bg-gray-800/40 rounded-xl border border-gray-700/40 divide-y divide-gray-700/40">

              {/* Plan row */}
              <div className="flex items-center justify-between px-4 py-3">
                <div className="flex items-center gap-2">
                  <CreditCard className="h-4 w-4 text-gray-400" />
                  <span className="text-sm text-gray-300">Current plan</span>
                </div>
                <span className={`text-xs font-bold px-3 py-1 rounded-full border ${tierColor}`}>
                  {tier}
                </span>
              </div>

              {/* Premium perks */}
              {isPremium && !cancelMsg.text && (
                <div className="flex items-center gap-2 px-4 py-3 text-sm text-green-400">
                  <CheckCircle className="h-4 w-4 flex-shrink-0" />
                  Full access to all Player DeepDive analytics
                </div>
              )}

              {/* Complimentary access note */}
              {isExempt && (
                <div className="px-4 py-3 text-xs text-gray-400">
                  Your account has complimentary access — no billing required.
                </div>
              )}

              {/* Feedback messages */}
              {cancelMsg.text && (
                <div className={`px-4 py-3 flex items-start gap-2 text-sm ${
                  cancelMsg.type === 'success' ? 'text-yellow-300' : 'text-red-300'
                }`}>
                  <AlertCircle className="h-4 w-4 mt-0.5 flex-shrink-0" />
                  {cancelMsg.text}
                </div>
              )}
              {subError && (
                <div className="px-4 py-3 text-sm text-red-300">{subError}</div>
              )}

              {/* Action */}
              <div className="px-4 py-4">
                {/* Paid premium user: show cancel button */}
                {isPremium && !isExempt && !cancelMsg.text && (
                  cancelConfirm ? (
                    /* Inline confirmation — no window.confirm() */
                    <div className="space-y-3">
                      <p className="text-xs text-gray-400">
                        You'll keep access until the end of your billing period. Are you sure you want to cancel?
                      </p>
                      <div className="flex gap-2">
                        <button
                          onClick={handleCancel}
                          disabled={cancelLoading}
                          className="flex-1 bg-red-600 hover:bg-red-500 disabled:opacity-50 text-white text-sm font-bold py-2.5 rounded-lg transition-colors"
                        >
                          {cancelLoading ? 'Cancelling…' : 'Yes, cancel subscription'}
                        </button>
                        <button
                          onClick={() => setCancelConfirm(false)}
                          disabled={cancelLoading}
                          className="flex-1 bg-gray-700 hover:bg-gray-600 text-gray-200 text-sm font-semibold py-2.5 rounded-lg transition-colors"
                        >
                          Keep it
                        </button>
                      </div>
                    </div>
                  ) : (
                    <button
                      onClick={() => setCancelConfirm(true)}
                      className="w-full flex items-center justify-center gap-2 bg-red-600/20 hover:bg-red-600/40 border border-red-500/50 hover:border-red-500 text-red-400 hover:text-red-300 text-sm font-semibold py-3 rounded-lg transition-all duration-200"
                    >
                      Cancel Subscription
                    </button>
                  )
                )}

                {/* Free user: show subscribe button */}
                {!isPremium && !cancelMsg.text && (
                  <button
                    onClick={handleSubscribe}
                    disabled={subLoading}
                    className="w-full flex items-center justify-center gap-2 bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-400 hover:to-orange-500 text-white py-2.5 px-4 rounded-lg text-sm font-bold transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {subLoading ? (
                      <>
                        <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                        Redirecting…
                      </>
                    ) : (
                      <>
                        <Zap className="h-4 w-4" />
                        Subscribe — $2.49/mo
                      </>
                    )}
                  </button>
                )}
              </div>

            </div>
          </section>
        </div>

        {/* Footer */}
        <div className="px-6 pb-5 flex-shrink-0 border-t border-gray-700/50 pt-4">
          <button
            onClick={onClose}
            className="w-full text-center text-sm text-gray-500 hover:text-gray-300 transition-colors py-1"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default AccountSettings;
